import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { DataTable, DataTableColumn } from '../../../components/DataTable/DataTable';
import { ToggleSwitch } from '../../../components/ToggleSwitch/ToggleSwitch';
import { SearchControl } from '../../../components/SearchControl/SearchControl';
import { ModalDialog, ModalType } from '../../../components/ModalDialog/ModalDialog';
import { MenuItemButton } from '../../../components/Buttons/Buttons';
import { SettingsPage } from '../../../components/SettingsPage/SettingsPage';
import { SettingCard } from '../../../components/SettingCards/SettingCard/SettingCard';
import { ACCOUNT_MANAGEMENT_STRINGS } from '../AccountManagementStrings';
import {
	MoreOptionsIcon,
	PencilIcon,
	TrashIcon,
} from '../../../Icons/AccountManagementIcons/AccountManagementIcons';
import './StoresPage.css';

export interface Store {
	id: string;
	name: string;
	url: string;
	showInMenu: boolean;
	isPrimary?: boolean;
	description?: string;
}

interface StoreFlyoutProps {
	storeId: string;
	storeName: string;
	anchorRect: DOMRect;
	onEdit: (storeId: string) => void;
	onRemove: (storeId: string) => void;
	onClose: () => void;
	triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const StoreFlyout: React.FC<StoreFlyoutProps> = ({
	storeId,
	storeName,
	anchorRect,
	onEdit,
	onRemove,
	onClose,
	triggerRef,
}) => {
	const flyoutRef = useRef<HTMLDivElement>(null);
	const flyoutHeight = 100; // 2×36px rows + 12px padding×2 + 4px gap
	const spaceBelow = window.innerHeight - anchorRect.bottom;
	const openUpward = spaceBelow < flyoutHeight;

	const style: React.CSSProperties = {
		position: 'fixed',
		right: window.innerWidth - anchorRect.right,
		zIndex: 1000,
		...(openUpward
			? { bottom: window.innerHeight - anchorRect.top + 4 }
			: { top: anchorRect.bottom + 4 }),
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				flyoutRef.current &&
				!flyoutRef.current.contains(target) &&
				!triggerRef.current?.contains(target)
			) {
				onClose();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [onClose]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	return ReactDOM.createPortal(
		<div
			className="store-flyout"
			style={style}
			ref={flyoutRef}
			role="menu"
			aria-label={ACCOUNT_MANAGEMENT_STRINGS.flyoutAriaLabel(storeName)}
		>
			<MenuItemButton
				role="menuitem"
				onClick={() => {
					onEdit(storeId);
					onClose();
				}}
			>
				<PencilIcon />
				<span>{ACCOUNT_MANAGEMENT_STRINGS.flyoutEdit}</span>
			</MenuItemButton>
			<MenuItemButton
				className="store-flyout-item--remove"
				role="menuitem"
				onClick={() => {
					onRemove(storeId);
					onClose();
				}}
			>
				<TrashIcon />
				<span>{ACCOUNT_MANAGEMENT_STRINGS.flyoutRemove}</span>
			</MenuItemButton>
		</div>,
		document.body
	);
};

interface StoresPageProps {
	stores?: Store[];
	onToggle?: (storeId: string, checked: boolean) => void;
	onMoreOptions?: (storeId: string) => void;
	onEdit?: (store: Store) => void;
	onRemove?: (storeId: string) => void;
}

export const StoresPage: React.FC<StoresPageProps> = ({
	stores: externalStores,
	onToggle,
	onMoreOptions,
	onEdit,
	onRemove,
}) => {
	const [stores, setStores] = useState<Store[]>(externalStores ?? []);
	const [searchQuery, setSearchQuery] = useState('');
	const [openFlyoutId, setOpenFlyoutId] = useState<string | null>(null);
	const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
	const [removeConfirmingStoreId, setRemoveConfirmingStoreId] = useState<string | null>(
		null
	);
	const triggerRef = useRef<HTMLButtonElement | null>(null);

	// Sync internal state when the parent provides a new stores array
	useEffect(() => {
		if (externalStores) {
			setStores(externalStores);
		}
	}, [externalStores]);

	const handleToggleChange = (storeId: string, checked: boolean) => {
		setStores((prev) =>
			prev.map((store) =>
				store.id === storeId ? { ...store, showInMenu: checked } : store
			)
		);
		if (onToggle) onToggle(storeId, checked);
	};

	const handleToggleFlyout = (
		storeId: string,
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		const btn = e.currentTarget as HTMLButtonElement;
		triggerRef.current = btn;
		const rect = btn.getBoundingClientRect();
		setAnchorRect(rect);
		setOpenFlyoutId((prev) => (prev === storeId ? null : storeId));
		if (onMoreOptions) onMoreOptions(storeId);
	};

	const handleEdit = (storeId: string) => {
		if (onEdit) {
			const storeToEdit = stores.find((s) => s.id === storeId);
			if (storeToEdit) onEdit(storeToEdit);
		}
	};

	const handleRemoveRequest = (storeId: string) => {
		setRemoveConfirmingStoreId(storeId);
	};

	const handleConfirmRemove = () => {
		if (!removeConfirmingStoreId) return;
		setStores((prev) => prev.filter((s) => s.id !== removeConfirmingStoreId));
		if (onRemove) onRemove(removeConfirmingStoreId);
		setRemoveConfirmingStoreId(null);
	};

	const handleCancelRemove = () => {
		setRemoveConfirmingStoreId(null);
	};

	const filteredStores = stores.filter(
		(store) =>
			store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			store.url.toLowerCase().includes(searchQuery.toLowerCase())
	);

	const storeColumns: DataTableColumn<Store>[] = [
		{
			key: 'showInMenu',
			header: ACCOUNT_MANAGEMENT_STRINGS.columnShowInMenu,
			width: '120px',
			render: (store) => (
				<ToggleSwitch
					checked={store.showInMenu}
					onChange={(checked) => handleToggleChange(store.id, checked)}
					settingId={`store-toggle-${store.id}`}
					ariaLabel={ACCOUNT_MANAGEMENT_STRINGS.toggleAriaLabel(store.name)}
				/>
			),
		},
		{
			key: 'name',
			header: ACCOUNT_MANAGEMENT_STRINGS.columnStoreName,
			render: (store) => (
				<div className="store-name-cell">
					<div className="store-name-content">
						<div className="store-name-row">
							<span className="store-name-text">{store.name}</span>
							{store.isPrimary && (
								<span className="store-primary-badge">
									{ACCOUNT_MANAGEMENT_STRINGS.primaryBadge}
								</span>
							)}
						</div>
						<span className="store-url-text" title={store.url}>
							{store.url}
						</span>
					</div>
					<div className="store-actions-wrapper">
						<button
							className="store-more-options-btn"
							type="button"
							aria-label={ACCOUNT_MANAGEMENT_STRINGS.moreOptionsAriaLabel(store.name)}
							aria-haspopup="menu"
							aria-expanded={openFlyoutId === store.id}
							onClick={(e) => handleToggleFlyout(store.id, e)}
						>
							<MoreOptionsIcon />
						</button>
						{openFlyoutId === store.id && anchorRect && (
							<StoreFlyout
								storeId={store.id}
								storeName={store.name}
								anchorRect={anchorRect}
								onEdit={handleEdit}
								onRemove={handleRemoveRequest}
								onClose={() => setOpenFlyoutId(null)}
								triggerRef={triggerRef}
							/>
						)}
					</div>
				</div>
			),
		},
	];

	return (
		<SettingsPage
			title={ACCOUNT_MANAGEMENT_STRINGS.storesTitle}
			headerAction={
				<SearchControl
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					placeholder={ACCOUNT_MANAGEMENT_STRINGS.searchPlaceholder}
				/>
			}
			settings={
				<>
					<SettingCard
						title=""
						settingId="stores-list"
						description=""
						className="stores-card"
						content={
							<>
								<DataTable<Store>
									columns={storeColumns}
									data={filteredStores}
									keyExtractor={(store) => store.id}
									className="stores-data-table"
									ariaLabel={ACCOUNT_MANAGEMENT_STRINGS.tableAriaLabel}
									resizable
								/>

								{filteredStores.length === 0 && (
									<div className="stores-empty-state">
										<p>{ACCOUNT_MANAGEMENT_STRINGS.emptyState}</p>
									</div>
								)}
							</>
						}
					/>

					{/* Remove Store Confirmation Modal */}
					<ModalDialog
						isOpen={removeConfirmingStoreId !== null}
						onClose={handleCancelRemove}
						type={ModalType.Warning}
						title={ACCOUNT_MANAGEMENT_STRINGS.removeModalTitle(
							stores.find((s) => s.id === removeConfirmingStoreId)?.name ?? ''
						)}
						description={ACCOUNT_MANAGEMENT_STRINGS.removeModalDescription}
						primaryButton={{
							buttonText: ACCOUNT_MANAGEMENT_STRINGS.removeModalConfirmButton,
							onClick: handleConfirmRemove,
						}}
						secondaryButton={{
							buttonText: ACCOUNT_MANAGEMENT_STRINGS.removeModalCancelButton,
							onClick: handleCancelRemove,
						}}
					/>
				</>
			}
		/>
	);
};
